import { Link } from 'react-router-dom';
import { useActiveSection } from '../hooks/useActiveSection';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import FloatingFAQ from '@/components/FloatingFAQ';
import HeroProfessional from '@/components/HeroProfessional';
import { ScrollReveal } from '@/components/ScrollReveal';
import { 
  ArrowRight, 
  Bike,
  Building2,
  Calendar,
  CheckCircle,
  Settings,
  Monitor,
  Globe,
  BarChart3,
  FileText,
  UserCheck,
  Users,
  Wrench,
  Shield,
  Recycle,
  Star,
  MapPin,
  Heart,
  Target,
  Clock,
  Handshake,
  Award,
  User,
  Zap,
  Mountain,
  Route,
  TreePine,
  Coffee
} from 'lucide-react';

const QuiSommesNousRework = () => {
  const sectionIds = [
    'pourquoi-les-hirondelles',
    'notre-histoire',
    'facilitateur-activite',
    'notre-modele',
    'nos-offres',
    'experience-client',
    'saisonniers',
    'velos-reconditionnes',
    'notre-vision'
  ];
  const { activeSection, scrollToSection } = useActiveSection(sectionIds, 140);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <HeroProfessional
        title="Qui sommes-nous"
        subtitle="Opérateurs de services vélo intégrés pour les campings et lieux touristiques"
        keyPoints={[
          { icon: Target, text: "Facilitateurs d'activité" },
          { icon: Handshake, text: "Partenaires terrain" },
          { icon: Settings, text: "Solutions adaptées" }
        ]}
        backgroundImage="./images/editorial_field_stories_illustration_20260109_172240.png"
        imageAlt="Les Hirondelles - Opérateurs services vélo"
        actionButtons={[
          {
            text: "Découvrir nos solutions",
            href: "/professionnels",
            type: "primary",
            title: "Voir nos solutions pour campings",
            ariaLabel: "Accéder aux solutions professionnelles"
          },
          {
            text: "Nous contacter",
            href: "/nous-contacter",
            type: "secondary",
            title: "Prendre contact avec notre équipe",
            ariaLabel: "Formulaire de contact"
          }
        ]}
      />

      {/* Navigation interne */}
      <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center justify-center py-4">
            <div className="flex items-center space-x-1 bg-slate-100 rounded-full p-1 overflow-x-auto">
              <button 
                onClick={() => scrollToSection('pourquoi-les-hirondelles')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                  activeSection === 'pourquoi-les-hirondelles' 
                    ? 'bg-primary text-white shadow-sm' 
                    : 'text-slate-600 hover:text-primary hover:bg-white'
                }`}
              >
                Pourquoi Les Hirondelles
              </button>
              <button 
                onClick={() => scrollToSection('notre-histoire')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                  activeSection === 'notre-histoire' 
                    ? 'bg-primary text-white shadow-sm' 
                    : 'text-slate-600 hover:text-primary hover:bg-white'
                }`}
              >
                Notre histoire
              </button>
              <button 
                onClick={() => scrollToSection('facilitateur-activite')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                  activeSection === 'facilitateur-activite' 
                    ? 'bg-primary text-white shadow-sm' 
                    : 'text-slate-600 hover:text-primary hover:bg-white'
                }`}
              >
                Facilitateur d'activité
              </button>
              <button 
                onClick={() => scrollToSection('notre-modele')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                  activeSection === 'notre-modele' 
                    ? 'bg-primary text-white shadow-sm' 
                    : 'text-slate-600 hover:text-primary hover:bg-white'
                }`}
              >
                Notre modèle
              </button>
              <button 
                onClick={() => scrollToSection('nos-offres')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                  activeSection === 'nos-offres' 
                    ? 'bg-primary text-white shadow-sm' 
                    : 'text-slate-600 hover:text-primary hover:bg-white'
                }`}
              >
                Nos offres
              </button>
              <button 
                onClick={() => scrollToSection('experience-client')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                  activeSection === 'experience-client' 
                    ? 'bg-primary text-white shadow-sm' 
                    : 'text-slate-600 hover:text-primary hover:bg-white'
                }`}
              >
                Expérience client
              </button>
              <button 
                onClick={() => scrollToSection('saisonniers')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                  activeSection === 'saisonniers' 
                    ? 'bg-primary text-white shadow-sm' 
                    : 'text-slate-600 hover:text-primary hover:bg-white'
                }`}
              >
                Saisonniers
              </button>
              <button 
                onClick={() => scrollToSection('velos-reconditionnes')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                  activeSection === 'velos-reconditionnes' 
                    ? 'bg-primary text-white shadow-sm' 
                    : 'text-slate-600 hover:text-primary hover:bg-white'
                }`}
              >
                Vélos reconditionnés
              </button>
              <button 
                onClick={() => scrollToSection('notre-vision')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                  activeSection === 'notre-vision' 
                    ? 'bg-primary text-white shadow-sm' 
                    : 'text-slate-600 hover:text-primary hover:bg-white'
                }`}
              >
                Notre vision
              </button>
            </div>
          </nav>
        </div>
      </div>

      {/* 1️⃣ POURQUOI LES HIRONDELLES */}
      <section id="pourquoi-les-hirondelles" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-black mb-6">
                Pourquoi Les Hirondelles ?
              </h2>
              <p className="text-lg text-slate-600 max-w-4xl mx-auto leading-relaxed">
                Les Hirondelles accompagnent les campings dans la mise en place et l'exploitation 
                de services vélo intégrés. Nous facilitons l'accès au vélo pour les vacanciers 
                tout en déchargeant les gestionnaires des contraintes logistiques.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScrollReveal delay={100}>
              <Card className="border-slate-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Target className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-4">
                    Facilitateurs d'activité
                  </h3>
                  <p className="text-slate-600">
                    Nous rendons le vélo accessible et simple pour enrichir 
                    l'expérience vacances de vos clients.
                  </p>
                </CardContent>
              </Card>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <Card className="border-slate-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Handshake className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-4">
                    Partenaires terrain
                  </h3>
                  <p className="text-slate-600">
                    Une collaboration étroite avec les équipes pour une 
                    intégration parfaite dans votre établissement.
                  </p>
                </CardContent>
              </Card>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <Card className="border-slate-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Settings className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-4">
                    Solutions adaptées
                  </h3>
                  <p className="text-slate-600">
                    Chaque camping a ses spécificités. Nous adaptons 
                    nos services à votre contexte unique.
                  </p>
                </CardContent>
              </Card>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 2️⃣ NOTRE HISTOIRE */}
      <section id="notre-histoire" className="py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <ScrollReveal>
              <div>
                <h2 className="text-3xl font-bold text-black mb-6">
                  Notre histoire
                </h2>
                <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                  <p>
                    <strong className="text-primary">Les Hirondelles naissent d'une passion commune pour le vélo 
                    et d'une expertise terrain acquise sur la côte atlantique.</strong>
                  </p>
                  <p>
                    Constatant les difficultés des campings à proposer un service vélo de qualité 
                    (maintenance, gestion des stocks, formation du personnel), nous avons développé 
                    une approche intégrée qui libère les gestionnaires de ces contraintes.
                  </p>
                  <p>
                    Aujourd'hui, nous accompagnons des établissements de toutes tailles dans la 
                    mise en place de solutions vélo adaptées à leurs besoins et à leur clientèle.
                  </p>
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="relative">
                <img 
                  src="./images/bike_station_hero_20260211_111335.png" 
                  alt="Histoire Les Hirondelles"
                  className="w-full h-96 object-cover rounded-2xl shadow-lg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 3️⃣ FACILITATEUR D'ACTIVITÉ */}
      <section id="facilitateur-activite" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-3xl font-bold text-black mb-6">
              Facilitateur d'activité
            </h2>
            <p className="text-lg text-slate-600 max-w-4xl mx-auto">
              Nous transformons le vélo en véritable atout pour votre établissement
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <ScrollReveal delay={100}>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bike className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-black mb-2">Flotte adaptée</h3>
                <p className="text-sm text-slate-600">Vélos pour tous âges et tous niveaux</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wrench className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-black mb-2">Maintenance</h3>
                <p className="text-sm text-slate-600">Entretien régulier et réparations</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-black mb-2">Conseils parcours</h3>
                <p className="text-sm text-slate-600">Itinéraires adaptés à votre région</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={400}>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-black mb-2">Assurance</h3>
                <p className="text-sm text-slate-600">Couverture complète incluse</p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 4️⃣ NOTRE MODÈLE */}
      <section id="notre-modele" className="py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <ScrollReveal>
              <div className="relative">
                <img 
                  src="./images/workshop_professional_20260211_111332.png" 
                  alt="Modèle Les Hirondelles"
                  className="w-full h-96 object-cover rounded-2xl shadow-lg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div>
                <h2 className="text-3xl font-bold text-black mb-6">
                  Notre modèle
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-primary mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-black mb-2">Gestion complète</h3>
                      <p className="text-slate-600">
                        Nous prenons en charge l'intégralité du service vélo : 
                        de l'installation à la maintenance quotidienne.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-primary mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-black mb-2">Partenariat équilibré</h3>
                      <p className="text-slate-600">
                        Un modèle économique qui profite à tous : 
                        revenus partagés et investissement minimal.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-primary mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-black mb-2">Accompagnement terrain</h3>
                      <p className="text-slate-600">
                        Formation de vos équipes et support continu 
                        pour une intégration réussie.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 5️⃣ NOS OFFRES */}
      <section id="nos-offres" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-3xl font-bold text-black mb-6">
              Nos offres
            </h2>
            <p className="text-lg text-slate-600 max-w-4xl mx-auto">
              Des solutions adaptées à chaque type d'établissement
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ScrollReveal delay={100}>
              <Card className="border-slate-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-black">
                      Campings & Hébergements
                    </h3>
                  </div>
                  <p className="text-slate-600 mb-6">
                    Service vélo intégré avec gestion complète : flotte, maintenance, 
                    réservations et conseils parcours.
                  </p>
                  <Button asChild className="btn-primary">
                    <Link to="/professionnels">
                      En savoir plus
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <Card className="border-slate-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-black">
                      Collectivités
                    </h3>
                  </div>
                  <p className="text-slate-600 mb-6">
                    Solutions vélo pour communes, offices de tourisme et 
                    gestionnaires d'espaces naturels.
                  </p>
                  <Button asChild className="btn-primary">
                    <Link to="/collectivites">
                      Découvrir
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 6️⃣ EXPÉRIENCE CLIENT */}
      <section id="experience-client" className="py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-3xl font-bold text-black mb-6">
              Expérience client
            </h2>
            <p className="text-lg text-slate-600 max-w-4xl mx-auto">
              Une expérience vélo simple et mémorable pour vos vacanciers
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScrollReveal delay={100}>
              <div className="text-center">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-primary">1</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">
                  Réservation simple
                </h3>
                <p className="text-slate-600">
                  Réservation directe à l'accueil ou en ligne, 
                  avec choix du vélo adapté.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="text-center">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-primary">2</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">
                  Vélo prêt à rouler
                </h3>
                <p className="text-slate-600">
                  Vélos entretenus, réglés et équipés 
                  (casques, antivols, cartes parcours).
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <div className="text-center">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-primary">3</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">
                  Support continu
                </h3>
                <p className="text-slate-600">
                  Assistance technique et conseils 
                  pendant toute la durée de location.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 7️⃣ SAISONNIERS */}
      <section id="saisonniers" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <ScrollReveal>
              <div>
                <h2 className="text-3xl font-bold text-black mb-6">
                  Saisonniers
                </h2>
                <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                  <p>
                    <strong className="text-primary">Nous proposons des solutions spécifiques 
                    pour les travailleurs saisonniers.</strong>
                  </p>
                  <p>
                    Abonnements mensuels, tarifs préférentiels et service de maintenance 
                    inclus pour faciliter la mobilité des équipes saisonnières.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <CheckCircle className="h-6 w-6 text-primary mr-3" />
                      <span className="font-semibold text-black">Tarifs préférentiels</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-6 w-6 text-primary mr-3" />
                      <span className="font-semibold text-black">Abonnements flexibles</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-6 w-6 text-primary mr-3" />
                      <span className="font-semibold text-black">Maintenance incluse</span>
                    </div>
                  </div>
                </div>
                
                <Button asChild size="lg" className="btn-primary mt-8">
                  <Link to="/demande-saisonnier">
                    Faire une demande
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="relative">
                <img 
                  src="./images/corporate_group_cycling_20260211_111333.png" 
                  alt="Saisonniers vélo"
                  className="w-full h-96 object-cover rounded-2xl shadow-lg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 8️⃣ VÉLOS RECONDITIONNÉS */}
      <section id="velos-reconditionnes" className="py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <ScrollReveal>
              <div className="relative">
                <img 
                  src="./images/image00008.jpeg" 
                  alt="Vélos reconditionnés"
                  className="w-full h-96 object-cover rounded-2xl shadow-lg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div>
                <h2 className="text-3xl font-bold text-black mb-6">
                  Vélos reconditionnés
                </h2>
                <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                  <p>
                    <strong className="text-primary">Donnez une seconde vie à nos vélos de location.</strong>
                  </p>
                  <p>
                    Nos vélos de location sont entièrement remis à neuf par nos mécaniciens 
                    et proposés à la vente avec des remises allant jusqu'à -60%.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <CheckCircle className="h-6 w-6 text-primary mr-3" />
                      <span className="font-semibold text-black">Garantie 6 mois</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-6 w-6 text-primary mr-3" />
                      <span className="font-semibold text-black">Remise jusqu'à -60%</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-6 w-6 text-primary mr-3" />
                      <span className="font-semibold text-black">Démarche éco-responsable</span>
                    </div>
                  </div>
                </div>
                
                <Button asChild size="lg" className="btn-primary mt-8">
                  <Link to="/velos-occasion">
                    Voir les vélos disponibles
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 9️⃣ NOTRE VISION */}
      <section id="notre-vision" className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-3xl font-bold mb-6">
              Notre vision
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
              Faire du vélo un vecteur de découverte et de plaisir accessible à tous, 
              tout en soutenant une économie locale et durable sur la côte atlantique.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90">
                <Link to="/nos-services">
                  Découvrir nos services
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              
              <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <Link to="/nous-contacter">
                  Nous contacter
                </Link>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <FloatingFAQ />
      <Footer />
    </div>
  );
};

export default QuiSommesNousRework;